import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import axiosInstance from '../../axiosConfig';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { ProfileContext } from '../../context/ProfileContext';
import { Link as RouterLink } from 'react-router-dom';
import { FormCard, FormContainer } from '../SharedFormStyles';
import ForgotPassword from './ForgotPassword';
import API_URL from '../../config';

export default function SignIn(props) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState({});
  const { setUserProfile, setUserRole } = React.useContext(ProfileContext);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const navigate = useNavigate();
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 1200);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);

    return () => window.removeEventListener('resize', checkViewport);
  }, []);


  // 🛡️ Validierung
  const validateInputs = (data) => {
    const newErrors = {};
    if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse.';
    }
    if (!data.password || data.password.length < 6) {
      newErrors.password = 'Passwort muss mindestens 6 Zeichen haben.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🚀 Login-Logik
  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    if (!validateInputs(Object.fromEntries(data))) return;

    setLoading(true);
    setErrorMessage('');
    setErrors({});

    try {
      const response = await axiosInstance.post('/auth/login', {
        email: data.get('email'),
        password: data.get('password'),
      }, {
        // wichtig, damit Cookies gesendet werden
        withCredentials: true,
      });
  
      if (response.data.success) {
        const newAccessToken = response.data.accessToken;
  
        // Access Token in localStorage speichern
        localStorage.setItem('authToken', newAccessToken);
        
        // Dann weiter wie gewohnt
        props.setIsAuthenticated(true);
        props.setSessionExpiredMessage('');
    
        const decodedToken = jwtDecode(newAccessToken);
    
        const profileResponse = await axiosInstance.get('/profile/user-profile', {
          headers: { Authorization: `${newAccessToken}` },
        });
    
        setUserProfile(profileResponse.data.profile);
        setUserRole(decodedToken.role);
        navigate('/pages/user-profile');
      }
    } catch (error) {
      console.error('Fehler beim Anmelden:', error);
    
      if (error.response) {
        switch (error.response.status) {
          case 401:
            setErrors({
              email: 'E-Mail-Adresse oder Passwort ist falsch.',
              password: 'E-Mail-Adresse oder Passwort ist falsch.',
            });
            break;
          case 403:
            setErrors({
              email: 'Ihre E-Mail-Adresse wurde noch nicht verifiziert.',
              password: '',
            });
            break;
          default:
            setErrorMessage('Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
        }
      } else {
        setErrorMessage('Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung.');
      }
    } finally {
      setLoading(false);
    }    
  };

  return (
    <ThemeProvider theme={createTheme({ palette: 'light' })}>
      <CssBaseline enableColorScheme />
      {isMobileView && (
        <Alert severity="warning" sx={{
          margin: '1rem auto',
          maxWidth: 600,
          width: 'calc(100% - 2rem)',
        }}>
          Diese App ist nicht für Mobilgeräte optimiert. Bitte verwenden Sie ein Tablet oder einen Desktop-Computer.
        </Alert>
      )}
      <FormContainer direction="column" justifyContent="space-between" sx={{ margin: '10vh auto', backgroundImage: 'none'}}>
        <FormCard variant="outlined">
          <Typography component="h1" variant="h4">Anmelden</Typography>
          {errorMessage && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl>
              <FormLabel>E-Mail</FormLabel>
              <TextField
                name="email"
                type="email"
                error={!!errors.email}
                helperText={errors.email}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Passwort</FormLabel>
              <TextField
                name="password"
                type="password"
                error={!!errors.password}
                helperText={errors.password}
              />
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'right',
                  mt: 1,
                }}
              >
                <Link
                  variant="body2"
                  onClick={() => setForgotPasswordOpen(true)}
                  sx={{
                    textDecoration: 'underline',
                    color: '#1976d2',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                  }}
                >
                  Passwort vergessen?
                </Link>
              </Typography>
            </FormControl>

            <Button type="submit" fullWidth variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Anmelden'}
            </Button>
            <Typography sx={{ textAlign: 'center' }}>
              Sie haben noch keinen Account?{' '}
              <Link component={RouterLink} to="/pages/register">Registrieren</Link>
            </Typography>
          </Box>
        </FormCard>
      </FormContainer>
      <ForgotPassword
        open={forgotPasswordOpen}
        handleClose={() => setForgotPasswordOpen(false)}
      />
    </ThemeProvider>
  );
}
